import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaBoxOpen, FaTruck, FaClock, FaCheckCircle, FaTimesCircle, FaBan, FaExclamationTriangle } from 'react-icons/fa';

interface OrderItem {
  productName: string;
  quantity: number;
  price: number;
  imageUrl: string;
}

interface Order {
  id: string;
  orderDate: string;
  totalAmount: number;
  status: string;
  delhiveryWaybill?: string;
  estimatedDeliveryDate?: string;
  orderItems: OrderItem[];
}

interface MyOrdersProps {
  userId: string;
}

const MyOrders: React.FC<MyOrdersProps> = ({ userId }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  
  // ✅ New States for Custom Modal
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false); // Loader inside modal

  const navigate = useNavigate();

  useEffect(() => {
    fetchMyOrders();
  }, [userId]);

  const fetchMyOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error("Please login to view orders.");
        navigate('/login');
        return;
      }
      const res = await axios.get(`https://www.ayushvatika.com/api/orders/user/${userId}`, {
      // const res = await axios.get(`http://localhost:8484/api/orders/user/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setOrders(res.data);
    } catch (error) {
      console.error("Failed to fetch orders", error);
    } finally {
      setLoading(false);
    }
  };

  // 1. Open Modal
  const openCancelModal = (orderId: string) => {
      setSelectedOrderId(orderId);
      setShowCancelModal(true);
  };

  // 2. Close Modal
  const closeCancelModal = () => {
      setShowCancelModal(false);
      setSelectedOrderId(null);
  };

  // 3. Confirm Cancel (API Call)
  const confirmCancelOrder = async () => {
    if (!selectedOrderId) return;

    setCancelling(true);
    try {
        const token = localStorage.getItem('token');
        await axios.put(`https://www.ayushvatika.com/api/orders/${selectedOrderId}/cancel`, {}, {
        // await axios.put(`http://localhost:8484/api/orders/${selectedOrderId}/cancel`, {}, {
            headers: { 'Authorization': `Bearer ${token}` }
        });
        
        toast.success("Order Cancelled Successfully");
        fetchMyOrders(); // Refresh List
        closeCancelModal(); // Close Modal
    } catch (error: any) {
        toast.error(error.response?.data || "Failed to cancel order");
        closeCancelModal();
    } finally {
        setCancelling(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'DELIVERED':
        return <span className="flex items-center gap-1 text-green-600 bg-green-100 px-3 py-1 rounded-full text-xs font-bold"><FaCheckCircle/> DELIVERED</span>;
      case 'CANCELLED':
        return <span className="flex items-center gap-1 text-red-600 bg-red-100 px-3 py-1 rounded-full text-xs font-bold"><FaTimesCircle/> CANCELLED</span>;
      case 'SHIPPED':
      case 'OUT_FOR_DELIVERY':
        return <span className="flex items-center gap-1 text-purple-600 bg-purple-100 px-3 py-1 rounded-full text-xs font-bold"><FaTruck/> ON THE WAY</span>;
      default:
        return <span className="flex items-center gap-1 text-blue-600 bg-blue-100 px-3 py-1 rounded-full text-xs font-bold"><FaClock/> PLACED</span>;
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-8 min-h-screen bg-gray-50 font-inter relative">
      <h1 className="text-3xl font-bold text-gray-800 mb-8">My Orders</h1>

      {loading ? (
        <div className="flex justify-center items-center h-64 text-gray-500">Loading your orders...</div>
      ) : orders.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl shadow-sm border border-gray-200">
          <FaBoxOpen className="mx-auto text-gray-300 mb-4" size={60} />
          <h2 className="text-xl font-bold text-gray-700">No Orders Yet</h2>
          <p className="text-gray-500 mb-6">Looks like you haven't bought anything yet.</p>
          <button onClick={() => navigate('/')} className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition">
            Start Shopping
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map((order) => (
            <div key={order.id} className="bg-white rounded-xl shadow-md border border-gray-100 overflow-hidden hover:shadow-lg transition-shadow duration-300">
              
              <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex flex-col md:flex-row justify-between md:items-center gap-4">
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Order ID</span>
                  <span className="font-mono font-bold text-gray-800">{order.id}</span>
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Date Placed</span>
                  <span className="font-medium text-gray-700">{new Date(order.orderDate).toLocaleDateString()}</span>
                </div>
                <div className="flex flex-col gap-1">
                    <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Total Amount</span>
                    <span className="font-bold text-gray-900">₹{order.totalAmount}</span>
                </div>
                <div>{getStatusBadge(order.status)}</div>
              </div>

              <div className="p-6">
                <div className="flex flex-col md:flex-row justify-between mb-6 gap-4 items-start">
                    <div>
                         {order.status !== 'CANCELLED' && order.status !== 'DELIVERED' && (
                             <p className="text-emerald-700 font-semibold text-sm">
                                Expected Delivery: {order.estimatedDeliveryDate || "Calculating..."}
                             </p>
                         )}
                         {order.status === 'DELIVERED' && <p className="text-gray-500 text-sm">Delivered on {order.estimatedDeliveryDate}</p>}
                         {order.status === 'CANCELLED' && <p className="text-red-500 text-sm font-bold">This order was cancelled.</p>}
                    </div>

                    <div className="flex gap-3">
                        {/* Track Button */}
                        {order.delhiveryWaybill && order.status !== 'CANCELLED' && (
                            <a 
                            href={`https://www.delhivery.com/track/package/${order.delhiveryWaybill}`}
                            target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg font-bold text-sm hover:bg-orange-600 transition shadow-sm"
                            >
                            <FaTruck /> Track
                            </a>
                        )}

                        {/* ✅ Cancel Button (Opens Modal) */}
                        {order.status === 'PLACED' && (
                            <button 
                                onClick={() => openCancelModal(order.id)}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-sm transition shadow-sm border bg-white text-red-600 border-red-200 hover:bg-red-50 hover:border-red-300"
                            >
                                <FaBan /> Cancel Order
                            </button>
                        )}
                    </div>
                </div>

                <div className="space-y-4">
                  {order.orderItems.map((item, index) => (
                    <div key={index} className="flex gap-4 items-center border-b last:border-0 pb-4 last:pb-0">
                       <img src={item.imageUrl || '/Logo/default-product.png'} alt={item.productName} className="w-16 h-16 object-cover rounded border border-gray-200"/>
                       <div className="flex-1">
                          <h4 className="font-bold text-gray-800">{item.productName}</h4>
                          <p className="text-sm text-gray-500">Quantity: {item.quantity}</p>
                       </div>
                       <div className="font-bold text-gray-700">₹{item.price}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ✅ CUSTOM CONFIRMATION MODAL */}
      {showCancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 transform transition-all scale-100">
                <div className="flex flex-col items-center text-center">
                    <div className="bg-red-100 p-3 rounded-full mb-4">
                        <FaExclamationTriangle className="text-red-600 text-2xl" />
                    </div>
                    
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Are you sure?</h2>
                    <p className="text-gray-500 mb-6">
                        Do you really want to cancel this order? This process cannot be undone.
                    </p>

                    <div className="flex gap-3 w-full">
                        <button 
                            onClick={closeCancelModal}
                            disabled={cancelling}
                            className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 font-semibold rounded-lg hover:bg-gray-200 transition"
                        >
                            No, Keep it
                        </button>
                        <button 
                            onClick={confirmCancelOrder}
                            disabled={cancelling}
                            className="flex-1 px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition flex justify-center items-center gap-2"
                        >
                            {cancelling ? (
                                <>Processing...</>
                            ) : (
                                <>Yes, Cancel Order</>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
      )}

    </div>
  );
};

export default MyOrders;